import { useAdditionalValuesPopup } from '../Providers/AdditionalValuesPopupProvider'
import Button from '../../../components/Button/Button'
import { BUTTON_TYPES } from '../../../components/Button'
import { BTN_TXT, LABELS, PLACEHOLDERS } from '../../../constants'
import Popup from '../../../components/Popup/Popup'
import { TAdditionalValueForm } from './types'
import { CustomForm } from 'components/Forms/CustomForm'
import { Input } from '../../../components/Input'
import { Select } from '../../../components/Select'
import { Typography } from '../../../components/Typography'
import { useAddEditAdditionalValuesForm } from './useAddEditAdditionalValuesForm'
import {
  numberIsGreaterThan0Validator,
  validateMessages
} from '../../../helper/common'
import { useSelector } from 'react-redux'
import { getUser } from '../../../redux/store/user/getters'
import { ROLES } from '../../Permission'
import { currencyFormatterTwoDecimals } from '../../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { useAdditionalValuesContext } from '../Providers/AdditionalValuesContextProvider'

export const AddEditAdditionalValueModal = ({
  onSaveCallback
}: {
  onSaveCallback: () => void
}) => {
  const user = useSelector(getUser)

  const canSeeHealthSystem = !user.outside_community
  const userCanChangeTheHSValue = user.role === ROLES.COMMUNITY_PRESIDENT

  const {
    state: { selectedAdditionalValue }
  } = useAdditionalValuesContext()

  const {
    form,
    errors,
    setErrors,
    typeOptions,
    subtypeOptions,
    hsOptions,
    hospitalOptions,
    sourcingOptions,
    categoryOptions,
    selectedType,
    selectedHS,
    selectedSourcingPerson,
    onSave
  } = useAddEditAdditionalValuesForm({
    predefinedHSValue: userCanChangeTheHSValue ? undefined : user.health_system,
    onSaveCallback
  })

  const {
    addEditAdditionalValuesPopup: { actions, state }
  } = useAdditionalValuesPopup()

  return (
    <Popup
      {...state}
      title={`${selectedAdditionalValue ? 'Edit' : 'Add'} ${state.title}`}
      width={910}
      footer={
        <>
          <Button type={BUTTON_TYPES.DEFAULT} onClick={actions.close} upperCase>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            form="add-additional-values-form"
            htmlType="submit"
            upperCase
          >
            {BTN_TXT.SAVE}
          </Button>
        </>
      }
    >
      <CustomForm<TAdditionalValueForm>
        form={form}
        id="add-additional-values-form"
        validateMessages={validateMessages}
        onFinish={onSave}
        onFieldsChange={() => setErrors({})}
        enableScrollToError
        showErrorMsgOnFail
      >
        <div className="column gap-24">
          <Typography.Body2 className="add-signatory-modal__hint">
            Record rebates or other savings not directly linked to contracts or
            RFPs.
          </Typography.Body2>
          <div className="row two-columns">
            <Input
              propsItem={{
                name: 'name',
                label: LABELS.NAME,
                help: errors.name?.[0],
                validateStatus: errors.name && 'error',
                rules: [
                  {
                    required: true
                  }
                ]
              }}
              propsInput={{
                showCount: true,
                maxLength: 100
              }}
            />
            <Input.Number
              propsItem={{
                name: 'amount_usd',
                label: LABELS.AMOUNT,
                help: errors.amount_usd?.[0],
                validateStatus: errors.amount_usd && 'error',
                rules: [
                  {
                    required: true
                  },
                  numberIsGreaterThan0Validator
                ]
              }}
              propsInputNumber={{
                min: '0',
                max: '9999999999.99',
                maxLength: 15,
                controls: false,
                formatter: currencyFormatterTwoDecimals,
                placeholder: '0'
              }}
            />
          </div>
          <div className="row two-columns">
            <Select
              options={typeOptions}
              propsItem={{
                label: LABELS.TYPE,
                name: 'type',
                help: errors.type?.[0],
                validateStatus: errors.type?.[0] && 'error',
                rules: [
                  {
                    required: true
                  }
                ]
              }}
              propsSelect={{
                required: true,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                onChange: () => form.resetFields(['subtype'])
              }}
            />
            <Select
              options={subtypeOptions}
              propsItem={{
                label: LABELS.SUBTYPE,
                name: 'subtype',
                help: errors.subtype?.[0],
                validateStatus: errors.subtype?.[0] && 'error',
                rules: [
                  {
                    required: !selectedType || subtypeOptions.length > 0
                  }
                ]
              }}
              propsSelect={{
                required: !selectedType || subtypeOptions.length > 0,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                disabled: !selectedType || !subtypeOptions.length
              }}
            />
          </div>
          <div className="row">
            <Input.TextArea
              propsItem={{
                name: 'description',
                label: LABELS.DESCRIPTION,
                rules: [
                  {
                    max: 250
                  }
                ]
              }}
              propsTextArea={{
                showCount: true,
                maxLength: 250
              }}
            />
          </div>
          <div className="row two-columns">
            <Select
              options={hsOptions}
              alwaysName={!canSeeHealthSystem}
              propsItem={{
                label: LABELS.HEALTH_SYSTEM,
                name: 'health_system',
                hidden: !canSeeHealthSystem,
                help: errors.health_system?.[0],
                validateStatus: errors.health_system?.[0] && 'error',
                rules: [
                  {
                    required: true
                  }
                ]
              }}
              propsSelect={{
                required: true,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                disabled: !userCanChangeTheHSValue,
                onChange: () => {
                  form.resetFields(['hospital', 'sourcing_person'])
                }
              }}
            />
            <Select
              options={hospitalOptions}
              propsItem={{
                label: LABELS.HOSPITAL,
                name: 'hospital'
              }}
              propsSelect={{
                allowClear: true,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                disabled: !selectedHS
              }}
            />
          </div>
          <div className="row two-columns">
            <Select
              options={sourcingOptions}
              propsItem={{
                label: LABELS.SOURCING_OWNER,
                name: 'sourcing_person',
                help: errors.sourcing_person?.[0],
                validateStatus: errors.sourcing_person?.[0] && 'error',
                rules: [
                  {
                    required: true
                  }
                ]
              }}
              propsSelect={{
                required: true,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                disabled: !selectedHS,
                onChange: () => form.resetFields(['category'])
              }}
            />
            <Select
              options={categoryOptions}
              propsItem={{
                label: LABELS.ASSOCIATED_CATEGORY,
                name: 'category'
              }}
              propsSelect={{
                allowClear: true,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                disabled: !selectedSourcingPerson
              }}
            />
          </div>
        </div>
      </CustomForm>
    </Popup>
  )
}
